import React, { useEffect, useState } from 'react';
import { Badge, Flex, H1, H3, P, SearchInput } from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import Info from '@decisiv/iconix/lib/components/Info';
import { useParams } from 'react-router-dom';
import {
  LineDivider,
  LogInspectorSection,
  LogListSection,
  LogSearchBarSection,
} from './style';
import LogList from './LogList';
import StepFunctionExecutionsApi from '../../api/step_function_executions';
import WrapperWithSearching from '../../components/WrapperWithSearching';
import Helmet from '../../components/SEO/Helmet';

export default function LogInspectorPage() {
  // v6 has no named splats; the wildcard segment is read as '*'.
  const { '*': traceId } = useParams();

  const [searchFailed, setSearchFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logItems, setLogItems] = useState([]);
  const [logsHistory, setLogsHistory] = useState([]);
  const [searchInput, setSearchInput] = useState('');

  async function getLogsByTraceId(urlTraceId) {
    const uuidToSearch = searchInput || urlTraceId;

    if (!uuidToSearch) {
      return;
    }

    setIsLoading(true);
    setSearchFailed(false);

    const responseLogs =
      await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByTraceId(
        uuidToSearch,
      );

    if (responseLogs.data && responseLogs.data.length > 0) {
      setLogItems(responseLogs.data);
    } else {
      setSearchFailed(true);
      setLogItems([]);
    }

    setIsLoading(false);
  }

  async function getLogsByParentTraceId(parentTraceId) {
    if (!parentTraceId) {
      return;
    }
    setIsLoading(true);
    setSearchFailed(false);

    const responseLogs =
      await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByParentTraceId(
        parentTraceId,
      );

    if (responseLogs.data && responseLogs.data.length > 0) {
      setLogItems(responseLogs.data);
      setSearchInput(responseLogs.data[0].attributes.x_decisiv_trace_id);
    } else {
      setSearchFailed(true);
      setLogItems([]);
    }

    setIsLoading(false);
  }

  const onChangeSearchInputHandler = (event) => {
    if (event.target.value === '') {
      setLogItems([]);
      setSearchFailed(false);
    }

    setSearchInput(event.target.value);
  };

  useEffect(() => {
    // The page survives a wildcard change, so everything scoped to the previous
    // trace goes before the next one is fetched — otherwise trace A's logs and
    // history stay on screen under trace B, or under the bare inspector URL.
    setLogItems([]);
    setLogsHistory([]);
    setSearchFailed(false);

    if (!traceId) {
      return undefined;
    }

    let cancelled = false;

    async function fetchLogsByTraceId() {
      setIsLoading(true);
      setSearchFailed(false);

      const responseLogs =
        await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByTraceId(
          traceId,
        );

      if (cancelled) {
        return;
      }

      if (responseLogs.data && responseLogs.data.length > 0) {
        setLogItems(responseLogs.data);
      } else {
        setSearchFailed(true);
        setLogItems([]);
      }

      setIsLoading(false);
    }

    fetchLogsByTraceId();

    return () => {
      cancelled = true;
    };
  }, [traceId]);

  useEffect(() => {
    if (logItems.length === 0) {
      setLogsHistory([]);
      return undefined;
    }

    // The request is not tied to the trace that started it, so a slow response
    // for the previous trace would land on top of the current one's history.
    let cancelled = false;

    async function getLogsHistory() {
      const response =
        await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByConfiguredIntegrationId(
          logItems[0].attributes.configured_integration_id,
        );

      if (cancelled) {
        return;
      }

      if (response.data && response.data.length > 0) {
        setLogsHistory(response.data);
      }
    }

    getLogsHistory();

    return () => {
      cancelled = true;
    };
  }, [logItems]);

  return (
    <>
      <Helmet title={t`Log Inspector`} description={t`Log Inspector`} />
      <LogInspectorSection
        flexDirection="column"
        justifyContent="center"
        padding={2}
        flex="none"
        flexGrow="0"
      >
        <Flex flexDirection="row">
          <Flex flex={1} title={t`Log Inspector`}>
            <H1>
              <Trans>Log Inspector</Trans>
            </H1>
          </Flex>
        </Flex>
        <P color="alaskanHusky">
          <Trans>
            Look up and inspect integration execution logs by trace ID. Search
            for a specific execution to view its details, timing, HTTP status,
            and retry failed runs.
          </Trans>
        </P>
      </LogInspectorSection>

      <LineDivider />
      <LogSearchBarSection>
        <Flex
          marginLeft={1}
          padding={1}
          name="SearchInputFlex"
          style={{ width: '800px' }}
        >
          <SearchInput
            value={searchInput}
            label={t`Search it`}
            name="input-name"
            onSearch={getLogsByTraceId}
            onChange={onChangeSearchInputHandler}
            size="medium"
            helpMessage={t`Search by X-Decisiv-Trace-Id.`}
          />
        </Flex>
      </LogSearchBarSection>

      <LogListSection flexDirection="column" name="LogListSection">
        {searchFailed && (
          <Flex
            alignItems="center"
            style={{ width: '100%' }}
            name="searchErrorBox"
            flexDirection="column"
          >
            <Flex>
              <Badge
                key="ErrorBadge"
                aria-label="Error Badge"
                color="information"
                icon={Info}
                size="large"
                palette="bright"
              />
            </Flex>
            <Flex marginTop={1}>
              <H3 weight="semibold">
                <Trans>No X-Decisiv-Trace-Id Found</Trans>
              </H3>
            </Flex>
            <Flex>
              <P size="small">
                <Trans>No X-Decisiv-Trace-Id were found with the ID</Trans>
              </P>
            </Flex>
            <Flex>
              <P color="alaskanHusky">{searchInput}</P>
            </Flex>
          </Flex>
        )}

        <WrapperWithSearching loading={isLoading}>
          {logItems.length > 0 && (
            <LogList
              logsList={logItems}
              logsHistory={logsHistory}
              isLoading={isLoading}
              onRefresh={getLogsByParentTraceId}
            />
          )}
        </WrapperWithSearching>
      </LogListSection>
    </>
  );
}
